import { OpenFeature } from "@openfeature/web-sdk";
import { ShoppingCartIcon } from "lucide-react";
import {
  Link,
  LoaderFunction,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
} from "react-router-dom";

import { get } from "@/api";
import type { Models } from "@/api/types";
import { Image, Text } from "@/atoms";
import { useCurrentOrderDetails } from "@/modules/cart/useCartDetails";
import { queryClient } from "@/pages/Root";
import LoadingLayout from "@/pages/root/LoadingLayout";
import Services from "@/services/Services";
import { waitObj } from "@/toolbox/Promise";
import { isNativePlatform } from "@/utils/platform.helpers";
import {
  extractPlatformStatus,
  PlatformAccessStatus,
} from "@/utils/platformAccessStatus";

export const PublicGiftCardLayout = () => {
  const location = useLocation();
  const { platform } = useLoaderData() as {
    platform: Models<"PlatformModel">;
  };

  const { data: cartDetails } = useCurrentOrderDetails();

  // Check if we're on the claim page to use V3 styling
  const isClaimPage = location.pathname.includes("/gift-cards/claim");

  const renderCartLink = () => {
    if (cartDetails) {
      const hasItems = (cartDetails.shopDisplayOrderLineItems?.length || 0) > 0;
      const totalAmount = cartDetails.estimatedTotal || 0;

      if (!hasItems) {
        return null;
      }

      return (
        <Link to="/gift-cards/cart">
          <div className="flex items-center gap-2 rounded-full bg-black px-4 py-2 transition-colors hover:bg-gray-800">
            <ShoppingCartIcon size={18} />

            <div className="relative">
              <Text
                variant="body2"
                text={`$${(totalAmount / 100).toFixed(0)}`}
                className="ml-2 text-white"
              />
            </div>
          </div>
        </Link>
      );
    }
  };

  const renderHeader = () => (
    <header className="fixed left-0 right-0 top-0 z-10 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 text-white">
      <div className="flex items-center gap-2">
        <div className="h-full w-24">
          <Image
            src={platform.logoImageUrl as string}
            alt="Platform logo"
            className="h-full w-24 object-contain"
          />
        </div>
      </div>
      <div className="flex flex-1  items-center justify-end gap-4">
        <Link to="/gift-cards/purchase">
          <Text
            variant="body1"
            text="Purchase"
            className="text-gray-500 transition-colors hover:text-gray-800"
          />
        </Link>
        {renderCartLink()}
      </div>
    </header>
  );

  // If it's the claim page, use V3 styling with MobileViewportContainer
  if (isClaimPage) {
    return (
      <>
        <LoadingLayout />

        <div className="PublicGiftCardLayout mx-auto flex min-h-screen w-full max-w-[390px] flex-col">
          <Outlet />
        </div>
      </>
    );
  }

  // Otherwise, use the standard layout with header
  return (
    <>
      <LoadingLayout />

      <div className="PublicGiftCardLayout">
        {renderHeader()}

        <div className="pt-16">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export const publicGiftCardLayoutLoader: LoaderFunction = async ({
  request,
}) => {
  let platform;
  try {
    platform = await queryClient.fetchQuery(["/platform/current"], () =>
      get("/platform/current").catch(() => null)
    );
  } catch (error) {
    console.error("Error fetching platform data:", error);
    throw redirect("/");
  }

  if (!platform?.platformSettings?.giftCardEnabled) {
    throw redirect("/");
  }

  await Services.auth.loadToken();

  const user = await queryClient.fetchQuery(["/user"], () =>
    get("/user").catch(() => null)
  );

  // Ensure we have a platform context for feature flagging targeting rules
  OpenFeature.setContext({
    platform: platform
      ? {
          targetingKey: platform.id,
        }
      : null,
  });

  // If the platform is not active, redirect to the deactivated page
  if (extractPlatformStatus(platform) === PlatformAccessStatus.Deactivated) {
    if (
      (isNativePlatform() && !request.url.includes("/signin")) ||
      !isNativePlatform()
    ) {
      return redirect("/deactivated");
    }

    return null;
  }

  if (platform && !platform.platformSettings?.giftCardEnabled) {
    return redirect("/signin");
  }

  const giftCards = await queryClient
    .fetchQuery(["/gift-cards/public", platform?.id], () =>
      get("/gift-cards/public/{platformId}/listings", {
        platformId: platform!.id!,
      })
    )
    .catch(() => []);

  if (!giftCards || giftCards.length === 0) {
    return redirect("/signin");
  }

  return waitObj({
    platform,
    user,
  });
};
